import React from 'react';
import { Flex, Text, Tooltip, useMantineTheme } from '@mantine/core';
import {
  IconBuilding,
  IconId,
  IconList,
  IconMapPin,
  IconTag,
  IconUser,
} from '@tabler/icons';
import { OrgFilterExtraInfoField } from '../../utils/org-filter-config';
import { getOrgExtraInfoBadgeItems } from '../../utils/company-filter-label';

type CompanyFilterExtraInfoLineProps = {
  org: Record<string, unknown> | null | undefined;
  extraInfoFields: OrgFilterExtraInfoField[];
};

const EXTRA_INFO_ICON_MAP: Record<string, React.ComponentType<{ size?: number }>> = {
  IconId,
  IconMapPin,
  IconBuilding,
  IconTag,
  IconUser,
  IconList,
};

export function resolveTablerIcon(iconName?: string): React.ComponentType<{ size?: number }> | null {
  if (!iconName) {
    return null;
  }

  return EXTRA_INFO_ICON_MAP[iconName] ?? null;
}

function renderExtraInfoSegment(
  item: ReturnType<typeof getOrgExtraInfoBadgeItems>[number],
  index: number,
  dimmedColor: string
): React.ReactNode {
  const IconComponent = resolveTablerIcon(item.icon);
  const segment = (
    <Flex key={`${item.displayValue}-${index}`} align="center" gap={4} component="span" c="dimmed">
      {IconComponent ? <IconComponent size={12} color={dimmedColor} /> : null}
      <Text span size="xs" lineClamp={1}>
        {item.displayValue}
      </Text>
    </Flex>
  );

  if (item.tooltip) {
    return (
      <Tooltip key={`${item.displayValue}-${index}`} label={item.tooltip} withArrow>
        {segment}
      </Tooltip>
    );
  }

  return segment;
}

export function CompanyFilterExtraInfoBadges({
  org,
  extraInfoFields,
}: CompanyFilterExtraInfoLineProps) {
  const theme = useMantineTheme();
  const dimmedColor =
    theme.colorScheme === 'dark' ? theme.colors.dark[2] : theme.colors.gray[6];

  if (!extraInfoFields.length) {
    return null;
  }

  const extraInfoItems = getOrgExtraInfoBadgeItems(org, extraInfoFields);

  return (
    <Flex align="center" gap={6} mt={2} wrap="wrap">
      {extraInfoItems.map((item, index) => (
        <React.Fragment key={`${item.displayValue}-${index}`}>
          {index > 0 ? (
            <Text span size="xs" c="dimmed">
              ·
            </Text>
          ) : null}
          {renderExtraInfoSegment(item, index, dimmedColor)}
        </React.Fragment>
      ))}
    </Flex>
  );
}
